import { useEffect, useState } from 'react';
import axios from 'axios';
import {
	API_ENDPOINTS,
	Card,
	CardBody,
	CardPlaceholder,
	customToast,
	DateFormats,
	formatDateFns,
	route,
	StringHelpers,
	SubTitle,
	useLangContext,
	Variants,
} from 'carrier-fe';
import { ApplianceAuditLogEntry } from '../../../types/appliance-management';

type Props = {
	applianceId: string;
	/** Bumped by the parent after any change, so an edit shows up without a page reload. */
	refetchKey: number;
};

/**
 * CUS-934's on-page audit log.
 *
 * Two things differ from the claim event log this is modelled on, and both need saying on the page rather
 * than only in a comment.
 *
 * It is merged across the appliance, its serial prefixes and its warranty options. The appliance's own
 * history only covers brand, type and name, so without the children the log would look almost empty while
 * an admin was making changes.
 *
 * And it records what changed and who changed it, but not the before and after values - the shared audit
 * resource suppresses those, with a follow-up ticket to add them. An admin who is not told that will
 * reasonably report the log as broken.
 */
function AuditLogSection({ applianceId, refetchKey }: Props) {
	const { crud, fields, responses } = useLangContext();

	const [entries, setEntries] = useState<ApplianceAuditLogEntry[]>([]);
	const [truncated, setTruncated] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	useEffect(() => {
		fetchLog();
	}, [applianceId, refetchKey]);

	const fetchLog = async () => {
		setIsLoading(true);

		try {
			const { data } = await axios.get(
				route(API_ENDPOINTS.ADMIN.VIESSMANN_APPLIANCE.AUDIT_LOG, {
					viessmannAppliance: applianceId,
				})
			);

			/*
			 * This endpoint reports facts about the read alongside the entries, so the payload is
			 * { rows, truncated, partitions_queried } rather than the bare array the claim log returns.
			 */
			setEntries(data?.data?.rows ?? []);
			setTruncated(!!data?.data?.truncated);
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'Error loading the audit log.',
				variant: Variants.Danger,
			});
		} finally {
			setIsLoading(false);
		}
	};

	if (isLoading) return <CardPlaceholder />;

	return (
		<Card className="mb-4">
			<CardBody>
				<SubTitle
					className="mb-1"
					style={{ color: '#464C5E' }}
					title={StringHelpers.title(
						crud?.sub_titles?.audit_log || 'Audit Log'
					)}
				/>
				<div className="text-muted mb-3">
					{fields?.appliance_audit_log_description ||
						'Changes to this appliance, its serial prefixes and its warranty options. Shows what changed and who changed it; the individual before and after values are not recorded yet.'}
				</div>

				{truncated && (
					<div className="text-warning mb-3">
						{fields?.audit_log_truncated ||
							'Showing the most recent history only. This appliance has more prefixes and warranty options than the log reads in one go, so older entries are not listed.'}
					</div>
				)}

				<div className="table-responsive">
					{entries.length > 0 ? (
						<table className="table w-100 mb-0">
							<thead>
								<tr>
									<th className="border border-gray-300 px-4 py-2">
										{StringHelpers.title(
											fields?.event_at || 'Date'
										)}
									</th>
									<th className="border border-gray-300 px-4 py-2">
										{StringHelpers.title(
											fields?.event_name || 'Event'
										)}
									</th>
									<th className="border border-gray-300 px-4 py-2">
										{StringHelpers.title(
											fields?.user_name || 'User'
										)}
									</th>
								</tr>
							</thead>
							<tbody>
								{entries.map((entry, index) => (
									<tr
										key={`${entry.event_name}-${entry.created_at}-${index}`}
									>
										<td className="border border-gray-300 px-4 py-2">
											{entry.created_at
												? formatDateFns(
														entry.created_at,
														DateFormats.DATETIME_SHORT
												  )
												: '-'}
										</td>
										<td className="border border-gray-300 px-4 py-2">
											{entry.display_event_name || '-'}
										</td>
										<td className="border border-gray-300 px-4 py-2">
											{entry.display_causer || '-'}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					) : (
						<p className="mb-0">
							{StringHelpers.replace(
								responses?.errors?.no_results_found ||
									'No :attribute found.',
								':attribute',
								responses?.models?.audit_log || 'audit log'
							)}
						</p>
					)}
				</div>
			</CardBody>
		</Card>
	);
}

export default AuditLogSection;
